import { useState } from 'react';
import { TeButtonMinimal, TeCallout } from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useMigrationCandidateUsers } from '../../../hooks/queries/migration.queries';
import SuggestedUsersModal from '../../../components/Migration/SuggestedUsersModal';
import featureFlags from '../../../utils/featureFlags';

const CalloutWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const CalloutAction = styled.div`
  position: absolute;
  right: 48px;
  top: 50%;
  transform: translateY(-50%);
`;

/**
 * Users-page Suggested Users callout (SPEC.md Q5 — placement, second of the
 * two required callouts). Self-contained: reads `useMigrationCandidateUsers`
 * directly and owns the review modal's open state, so `Users.tsx` only needs
 * to render this component above the filter bar.
 *
 * Re-checks `featureFlags.MIGRATION` explicitly (mirroring
 * `CustomerMigrationCallout`) rather than relying solely on the hook's own
 * `enabled` gate, because the hook still returns mock data whenever
 * `MIGRATION_MOCK_DATA` is on regardless of `MIGRATION`.
 *
 * Distinct copy from the Overview `UsersKPI` callout (`common:suggestedUsers.*`,
 * left untouched) — this one uses the new `users:suggestedUsersCallout.*`,
 * `suggestedUsersModal.*` and `suggestedUsersNotification.*` keys.
 *
 * `initialOpen` seeds the modal's open state on mount only (a one-shot deep
 * link from Overview's `?suggestedUsers=open` query param, see ADR 0005) —
 * it is not a controlled prop, so changes to it after mount are ignored.
 */
interface Props {
  initialOpen?: boolean;
}

function SuggestedUsersCallout({ initialOpen = false }: Props) {
  const { t } = useTranslation('users');
  const [modalOpen, setModalOpen] = useState(initialOpen);
  const { data } = useMigrationCandidateUsers();

  const hasSuggestedUsers = featureFlags.MIGRATION && !!data?.items?.length;

  if (!hasSuggestedUsers) return null;

  return (
    <>
      <CalloutWrapper>
        <TeCallout
          status="info"
          icon="info"
          heading={t('suggestedUsersCallout.heading')}
          text={t('suggestedUsersCallout.description')}
          grow
          style={{ borderColor: '#005FAD' }}
        />
        <CalloutAction>
          <TeButtonMinimal
            text={t('suggestedUsersCallout.action')}
            size="s"
            teclick={() => setModalOpen(true)}
          />
        </CalloutAction>
      </CalloutWrapper>

      <SuggestedUsersModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
}

export default SuggestedUsersCallout;
